import type { FactionTrustLevel, LoopState, PermanentState } from "../types"
import { factions, units } from "../game/content"
import { getTrust } from "../game/factionEngine"
import { getFactionCrestStyle, getFactionSpriteStyle } from "../game/sprites"
import {
  UnitPortrait,
  UnitSpecialty,
  UnitStatLine,
  UnitTraits,
} from "../components/UnitTraits"
import { FrameCorners } from "../components/Framed"
import type { Faction } from "../types"

interface FactionsScreenProps {
  permanent: PermanentState
  loop: LoopState
  onBack: () => void
}

const TRUST_LABEL: Record<FactionTrustLevel, string> = {
  unknown: "Unknown",
  recognized: "Recognized",
  trusted: "Trusted",
  subjected: "Subjected",
}

/**
 * A realm's standing, worn as a plain heraldic chip beside its name: where the
 * player stands with the faction. The crest now leads the entry above the
 * leader's portrait (see FactionEmblem), so the chip carries only the standing.
 */
function StandingChip({ standing }: { standing: string }) {
  return <span className="faction-standing">{standing}</span>
}

/**
 * A faction's heraldry at the head of its codex entry: the crest stacked above
 * the leader's portrait, so the institution's banner reads at a glance and the
 * face beneath it belongs to whoever commands it. Renders whichever cells the
 * sprite sheets provide; contributes nothing if the faction has neither.
 */
function FactionEmblem({ faction }: { faction: Faction }) {
  const crest = getFactionCrestStyle(faction.id)
  const portrait = getFactionSpriteStyle(faction.id)
  if (!crest && !portrait) return null
  return (
    <span className="faction-emblem">
      {crest && (
        <span className="faction-crest" style={crest} aria-hidden="true" />
      )}
      {portrait && (
        <span
          className="faction-portrait"
          style={portrait}
          role="img"
          aria-label={faction.name}
        />
      )}
    </span>
  )
}

/**
 * The units that fight under a faction's banner, drawn straight from units.json
 * by factionId — portrait, base numbers, and traits, so the codex doubles as a
 * bestiary. Shown only for realms the player has met (allies are always known;
 * hostile powers reveal their roster once encountered), so it never spoils a
 * force the player has not yet faced.
 */
function FactionRoster({ faction }: { faction: Faction }) {
  const roster = units.filter(
    (unit) => unit.factionId === faction.id && !unit.hideFromRoster,
  )
  if (roster.length === 0) return null
  return (
    <div className="faction-roster">
      <h4>Forces</h4>
      <ul>
        {roster.map((unit) => (
          <li key={unit.id} className="roster-unit">
            <UnitPortrait unit={unit} size="lg" />
            <div className="roster-unit-body">
              <span className="stack-name">{unit.name}</span>
              <UnitStatLine unit={unit} />
              <UnitTraits unit={unit} />
              <UnitSpecialty unit={unit} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The codex of the realms: who stands with the player and who stands against.
 * Split out from the Army screen (which handles the player's own contingents),
 * this half is a read-only reference — standing, leadership, weaknesses, and the
 * forces each realm can field — kept in step with factions.json.
 */
export function FactionsScreen({ permanent, loop, onBack }: FactionsScreenProps) {
  const alliedFactions = factions.filter((faction) => faction.allied)
  const hostileFactions = factions.filter((faction) => !faction.allied)
  const isResolved = (resolvedKnowledge: string | undefined) =>
    resolvedKnowledge ? permanent.knowledge.includes(resolvedKnowledge) : false

  return (
    <div className="screen factions-screen">
      <h1>Factions</h1>

      <h2>Allied Realms</h2>
      <ul className="faction-codex">
        {alliedFactions.map((faction) => {
          const trust = getTrust(loop, faction.id)
          const subjected = loop.subjectedFactions.includes(faction.id)
          return (
            <li key={faction.id} className="faction-codex-entry framed">
              <FrameCorners />
              <FactionEmblem faction={faction} />
              <div className="faction-codex-body">
                <h3>
                  {faction.name}{" "}
                  <StandingChip
                    standing={subjected ? "Subjected" : TRUST_LABEL[trust]}
                  />
                </h3>
                {faction.leader && (
                  <p>
                    <strong>Leader:</strong> {faction.leader}
                  </p>
                )}
                {faction.internalConflict && (
                  <p>
                    <strong>Internal conflict:</strong> {faction.internalConflict}
                  </p>
                )}
                {faction.submissionRequirement && !subjected && (
                  <p>
                    <strong>To bring under your command:</strong> {faction.submissionRequirement}
                  </p>
                )}
                <FactionRoster faction={faction} />
              </div>
            </li>
          )
        })}
      </ul>

      <hr className="ornament-rule" />

      <h2>Hostile Powers</h2>
      <ul className="faction-codex">
        {hostileFactions.map((faction) => {
          const resolved = isResolved(faction.resolvedKnowledge)
          const encountered = permanent.knowledge.includes(`encountered_faction_${faction.id}`)

          if (!encountered) {
            return <li key={faction.id} className="faction-codex-entry">
              UNKNOWN FACTION
            </li>
          }
          const status = resolved
            ? faction.id === "necromancer"
              ? "Reached"
              : "Defeated or redeemed"
            : encountered
              ? "Encountered"
              : "Not yet faced"
          return (
            <li key={faction.id} className="faction-codex-entry framed">
              <FrameCorners />
              <FactionEmblem faction={faction} />
              <div className="faction-codex-body">
                <h3>
                  {faction.name} <StandingChip standing={status} />
                </h3>
                {faction.leader && (
                  <p>
                    <strong>Leader:</strong> {faction.leader}
                  </p>
                )}
                {faction.weakness && (
                  <p>
                    <strong>Weakness:</strong> {faction.weakness}
                  </p>
                )}
                {faction.resolution && !resolved && (
                  <p>
                    <strong>Resolution:</strong> {faction.resolution}
                  </p>
                )}
                <FactionRoster faction={faction} />
              </div>
            </li>
          )
        })}
      </ul>

      <button type="button" onClick={onBack}>
        Back
      </button>
    </div>
  )
}
